import React from "react";
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Grid,
  GridItem,
  Image,
} from "@chakra-ui/react";
import ServiceDetailsHeader from "../../components/Common/ImageHeader";
import ExploreSection from "../../components/ProductDetails/ExploreSection";
import ServiceCard from "../../components/Common/ServiceCard";

const APIIntegrationPage = () => {
  // API Integration Services Data
  const apiServices = [
    {
      id: 1,
      title: "Custom API Development",
      description:
        "Tailored APIs that meet your unique business rules and workflows, ensuring smooth communication between software systems.",
      icon: "/svg/Custom_API_Development.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title: "Third-Party Integration",
      description:
        "Seamlessly connect with payment gateways, SMS gateways, document management, and cloud service providers to extend your platform's capabilities.",
      icon: "/svg/Third_Party_Integration.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 3,
      title: "API Gateway & Management",
      description:
        "Robust API gateways that control access, monitor usage, and enforce security policies, ensuring reliable and scalable service delivery.",
      icon: "/svg/API_Gateway_Management.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 4,
      title: "Microservices Architecture",
      description:
        "Decompose monolithic applications into microservices connected via APIs for improved agility, maintainability, and faster deployment cycles.",
      icon: "/svg/Microservices_Architecture.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 5,
      title: "API Testing & Documentation",
      description:
        "Comprehensive testing to ensure performance and security, accompanied by clear, developer-friendly documentation for easy adoption.",
      icon: "/svg/API_Testing_Documentation.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
  ];

  // Benefits Data
  const clientBenefits = [
    {
      id: 1,
      title: "Accelerated Digital Transformation",
      description:
        "Rapidly integrate new digital services without disrupting existing operations.",
    },
    {
      id: 2,
      title: "Cost Savings",
      description:
        "Reduce development and maintenance costs through reusable APIs and automated data exchange.",
    },
    {
      id: 3,
      title: "Future-Ready Platforms",
      description:
        "Build scalable, flexible architectures that adapt to evolving technology and policy needs.",
    },
    {
      id: 4,
      title: "Improved Citizen Experience",
      description:
        "Provide seamless, unified service access across multiple channels—web, mobile, kiosks—with consistent data and workflows.",
    },
    {
      id: 5,
      title: "Enhanced Collaboration",
      description:
        "Enable multiple government agencies and private sector partners to securely share data and services.",
    },
  ];

  // Reusable Benefit Card Component
  const BenefitCard = ({ benefit }) => (
    <GridItem key={benefit.id}>
      <Box
        bg="white"
        borderRadius="xl"
        p={6}
        border="1px"
        borderColor="gray.200"
        shadow="sm"
        h="full"
        transition="all 0.3s ease"
        _hover={{
          shadow: "md",
          transform: "translateY(-2px)",
          borderColor: "blue.200",
        }}
      >
        <VStack align="start" spacing={4}>
          <Heading size="md" color="#000B8C" fontWeight="bold">
            {benefit.title}
          </Heading>
          <Text color="gray.600" fontSize="sm" lineHeight="1.6">
            {benefit.description}
          </Text>
        </VStack>
      </Box>
    </GridItem>
  );

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>API Integration Services | Secure & Scalable Digital Connectivity in Bangladesh</title>
        <meta 
          name="description" 
          content="Business Automation Ltd. offers expert API integration services in Bangladesh, enabling seamless connection of government and enterprise platforms with secure, scalable, and efficient digital ecosystems." 
        />
        <meta 
          name="keywords" 
          content="API integration Bangladesh, government API integration, secure API management, third-party API Bangladesh, payment gateway API integration, SMS gateway API, microservices Bangladesh, digital transformation Bangladesh, e-Governance API, API security Bangladesh" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/services/api-integration" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/services/api-integration" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="API Integration Services | Secure & Scalable Digital Connectivity in Bangladesh" />
        <meta property="og:description" content="Business Automation Ltd. offers expert API integration services in Bangladesh, enabling seamless connection of government and enterprise platforms with secure, scalable, and efficient digital ecosystems." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/services/api-integration" />
        <meta name="twitter:title" content="API Integration Services | Secure & Scalable Digital Connectivity in Bangladesh" />
        <meta name="twitter:description" content="Business Automation Ltd. offers expert API integration services in Bangladesh, enabling seamless connection of government and enterprise platforms with secure, scalable, and efficient digital ecosystems." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ServiceDetailsHeader
        title="API Integration Solutions"
        subtitle="Seamless System Connectivity & Data Integration Solutions"
        backgroundImage="/png/bg_api.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/our-services" },
          {
            label: "API Integration Solutions",
            href: "#",
            isCurrentPage: true,
          },
        ]}
      />

      <Box
        as="section"
        py={{ base: 4, md: 6 }}
        sx={{
          backgroundImage: "url('/png/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
        }}
      >
        <Container maxW="7xl">
          <VStack align="start">
            <Text>
              At Business Automation Limited, we understand that today’s digital
              transformation requires systems to work together flawlessly. Our
              API Integration services enable government agencies, enterprises,
              and development partners in Bangladesh to connect disparate
              applications, platforms, and services into a cohesive, efficient
              digital ecosystem.
            </Text>
            <Text>
              By bridging data and functionalities across platforms, our API
              solutions accelerate innovation, improve service delivery, and
              enable real-time decision-making — essential for building the
              Digital Bangladesh vision.
            </Text>
            <Heading
              pt={8}
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color="gray.800"
            >
              Why{" "}
              <Text as="span" color="#000B8C">
                API Integration Matters{" "}
              </Text>
            </Heading>
            <Box w="full" display="flex" justifyContent="center" py={8}>
              <Box
                as="img"
                src="/png/apiInte.png"
                alt="AI-driven cybersecurity capabilities"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                loading="lazy"
                draggable="false"
              />
            </Box>

            {/* Our API Integration Services Section */}
            <Box w="full" py={8}>
              <Heading
                size="xl"
                textAlign="center"
                mb={8}
                color="gray.800"
                fontWeight="bold"
              >
                Our{" "}
                <Text as="span" color="#000B8C">
                  API Integration
                </Text>{" "}
                Services
              </Heading>

              {/* First Row: 3 Cards */}
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                gap={6}
                mb={6}
              >
                {apiServices.slice(0, 3).map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </Grid>

              {/* Second Row: 2 Cards */}
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={6}
                maxW="800px"
                mx="auto"
              >
                {apiServices.slice(3, 5).map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </Grid>
            </Box>

            {/* Benefits for Our Clients Section */}
            <Box w="full" py={8}>
              <Container maxW="7xl">
                <Heading
                  size="xl"
                  textAlign="center"
                  mb={8}
                  color="gray.800"
                  fontWeight="bold"
                >
                  Benefits for{" "}
                  <Text as="span" color="#000B8C">
                    Our Clients
                  </Text>
                </Heading>

                {/* Benefits Grid - 2 columns layout as shown in image */}
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={6}
                  maxW="7xl"
                  mx="auto"
                  justifyContent="center"
                  alignItems="stretch"
                >
                  {clientBenefits.map((benefit) => (
                    <Box
                      key={benefit.id}
                      w="100%"
                      maxW="500px"
                      minW={{ base: "100%", md: "400px" }}
                      flex={{ base: "1 1 100%", md: "1 1 calc(50% - 12px)" }}
                    >
                      <BenefitCard benefit={benefit} />
                    </Box>
                  ))}
                </Box>
              </Container>
            </Box>

            {/* Trusted API Integration Partner Section */}
            <Box w="full" py={12}>
              <Container maxW="7xl">
                <Grid
                  templateColumns={{ base: "1fr", lg: "2fr 1fr" }}
                  gap={12}
                  alignItems="center"
                >
                  {/* Left Content */}
                  <VStack align="start" spacing={6}>
                    <Heading
                      fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                      fontWeight="bold"
                      color="gray.800"
                      lineHeight="shorter"
                    >
                      Trusted{" "}
                      <Text as="span" color="#000B8C">
                        API Integration Partner
                      </Text>{" "}
                      in Bangladesh
                    </Heading>

                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      color="gray.600"
                      align={"justify"}
                      lineHeight="1.4"

                    >
                      With over 47 government, financial, and private sector
                      entities already connected through our API frameworks,
                      Business Automation Limited has established itself as one of
                      Bangladesh’s most trusted API integration partners. Our
                      integration experience with the different systems of
                      Office of the Registrar of Joint Stock Companies and Firms
                      (RJSC), Chief Controller of Imports & Exports (CCI&E),
                      Department of Immigration and Passport, National ID,
                      National Board of Revenue (NBR), City Corporations,
                      Financial Institutions, Water Supply and Sewerage
                      Authorities, Gas Distribution Companies, Office of The
                      Chief Inspector of Boilers, Chambers of Commerce, and many
                      other organizations—each benefiting from secure,
                      interoperable, and future-ready digital ecosystems built
                      by our team.
                    </Text>
                  </VStack>

                  {/* Right Image */}
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Image
                      src="/svg/hand_shaking.svg"
                      alt="Business Partnership Illustration"
                      maxW="100%"
                      h="auto"
                      objectFit="contain"
                    />
                  </Box>
                </Grid>
              </Container>
            </Box>
          </VStack>

          <ExploreSection />
        </Container>
      </Box>
    </>
  );
};

export default APIIntegrationPage;
